import { useCallback, useEffect, useRef, useState } from 'react';
import { ACEPTA, claseDe, etiquetaExtension, motivoDeRechazo, type LimitesMb } from './adjuntos';

/**
 * Adjuntos (imágenes, vídeos y documentos) elegidos ANTES de que la tarea exista.
 *
 * POR QUÉ NO SE REUTILIZA `Adjuntos`
 * Aquel componente habla con el servidor: lista, sube y borra contra un `workItemId`. Aquí
 * todavía no hay id —la tarea no se ha creado—, así que no hay nada que listar ni que
 * borrar. Son dos cosas distintas con el mismo aspecto, y forzarlas en un componente con un
 * `modo` haría que cada arreglo de una tuviera que pensarse para la otra.
 *
 * Lo que sí comparten son las REGLAS de qué adjunto vale, y eso vive en `adjuntos.ts`.
 *
 * ES CONTROLADO: los ficheros los guarda el editor, no este componente. Quien sube es el
 * editor, porque la subida solo puede ocurrir después de crear la tarea y obtener su id —y
 * el estado de algo tiene que vivir donde está la lógica que lo usa.
 *
 * NADA SE SUBE TODAVÍA. Los ficheros no salen del navegador hasta que se pulsa «Crear
 * tarea»; si se cancela el alta, no queda nada en el servidor. La pantalla lo dice, porque
 * una miniatura ya visible se lee como «ya está guardada».
 */
export function AdjuntosAlAlta({
  adjuntos,
  onCambiar,
  limites,
}: {
  adjuntos: File[];
  onCambiar: (adjuntos: File[]) => void;
  /** Los techos del servidor, que llegan en la sesión. No se adivinan aquí. */
  limites: LimitesMb;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [vistas, setVistas] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  // Los tres números por separado: el objeto `limites` se crea de nuevo en cada render de
  // quien lo pasa, y como dependencia volvería a registrar el listener de pegado cada vez.
  const { imagen, video, documento } = limites;

  // Las miniaturas son URLs de objeto: hay que revocarlas al cambiar la lista o al
  // desmontar, o cada fichero elegido se queda en memoria hasta recargar la página. Con
  // vídeos de decenas de MB eso deja de ser un detalle.
  useEffect(() => {
    const urls = adjuntos.map((f) => URL.createObjectURL(f));
    setVistas(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [adjuntos]);

  const añadir = useCallback(
    (nuevos: File[]) => {
      const aceptados: File[] = [];
      const motivos: string[] = [];
      for (const f of nuevos) {
        const motivo = motivoDeRechazo(f, { imagen, video, documento });
        if (motivo) motivos.push(motivo);
        else aceptados.push(f);
      }
      // Se aceptan los buenos y se explican los malos, en vez de descartar el lote entero:
      // arrastrar cuatro capturas y perder las cuatro porque una pesaba de más obliga a
      // repetir un trabajo que ya estaba hecho.
      setError(motivos.length > 0 ? motivos.join(' ') : null);
      if (aceptados.length > 0) onCambiar([...adjuntos, ...aceptados]);
    },
    [adjuntos, imagen, video, documento, onCambiar],
  );

  // Pegar una captura con Ctrl+V, igual que en una tarea ya creada: es como llega la
  // mayoría de las capturas, y obligar a guardarlas antes en un fichero es un paso que
  // nadie quiere dar. También vale un fichero copiado desde el explorador.
  useEffect(() => {
    const alPegar = (evento: ClipboardEvent): void => {
      const archivos = Array.from(evento.clipboardData?.files ?? []).filter(
        (f) => claseDe(f) !== null,
      );
      if (archivos.length > 0) {
        evento.preventDefault();
        añadir(archivos);
      }
    };
    window.addEventListener('paste', alPegar);
    return () => window.removeEventListener('paste', alPegar);
  }, [añadir]);

  const quitar = (indice: number): void => {
    onCambiar(adjuntos.filter((_, i) => i !== indice));
    setError(null);
  };

  return (
    <section className="border-t border-[var(--color-border-subtle)] pt-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[12px] text-[var(--color-ink-muted)]">
          Adjuntos {adjuntos.length > 0 && `(${adjuntos.length})`}
        </span>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="text-[12px] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]"
        >
          + Añadir
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACEPTA}
        multiple
        className="hidden"
        onChange={(e) => {
          añadir(Array.from(e.target.files ?? []));
          e.target.value = ''; // permite volver a elegir el mismo fichero
        }}
      />

      {error && (
        <p className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
          {error}
        </p>
      )}

      {adjuntos.length === 0 ? (
        <p className="mt-1 text-[11px] leading-snug text-[var(--color-ink-muted)]">
          Puedes pegar una captura con Ctrl+V o usar «Añadir» para elegir imágenes, vídeos o
          documentos (PDF, Office, Markdown, texto). Se suben al crear la tarea.
        </p>
      ) : (
        <>
          <ul className="mt-2 grid grid-cols-4 gap-2">
            {adjuntos.map((f, i) => (
              <li key={`${f.name}-${f.size}-${i}`} className="group relative">
                {claseDe(f) === 'documento' ? (
                  <div
                    title={`${f.name} · ${(f.size / 1024).toFixed(0)} KB`}
                    className="flex aspect-square w-full flex-col items-center justify-center gap-1 overflow-hidden rounded-lg border border-[var(--color-border-subtle)] p-2 text-center"
                  >
                    <span className="rounded bg-[var(--color-ink)] px-1.5 text-[10px] font-semibold leading-4 text-[var(--color-surface)]">
                      {etiquetaExtension(f.name)}
                    </span>
                    <span className="line-clamp-2 break-all text-[11px] leading-tight text-[var(--color-ink-muted)]">
                      {f.name}
                    </span>
                  </div>
                ) : f.type.startsWith('video/') ? (
                  // `preload="metadata"` para que se vea el primer fotograma sin descargar
                  // el vídeo entero: aquí solo hace falta reconocer cuál es.
                  <video
                    src={vistas[i]}
                    preload="metadata"
                    muted
                    title={`${f.name} · ${(f.size / 1024 / 1024).toFixed(1)} MB`}
                    className="aspect-square w-full rounded-lg border border-[var(--color-border-subtle)] bg-black object-cover"
                  />
                ) : (
                  <img
                    src={vistas[i]}
                    alt={f.name}
                    title={`${f.name} · ${(f.size / 1024).toFixed(0)} KB`}
                    className="aspect-square w-full rounded-lg border border-[var(--color-border-subtle)] object-cover"
                  />
                )}
                {f.type.startsWith('video/') && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 flex items-center justify-center text-[20px] text-white/90 drop-shadow"
                  >
                    ▶
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => quitar(i)}
                  aria-label={`Quitar ${f.name}`}
                  className="absolute right-1 top-1 rounded bg-black/70 px-1.5 text-[11px] leading-5 text-white opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          <p className="mt-1 text-[11px] text-[var(--color-ink-muted)]">
            Todavía no se han subido: se envían al crear la tarea.
          </p>
        </>
      )}
    </section>
  );
}
